import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';

const FORM_URL = 'https://readdy.ai/api/form/d7kdndl0lfokr4m6je10';

const productTypes = [
  'Hard Capsules',
  'Soft Gels',
  'Tablets',
  'Chewable Tablets',
  'Powder / Granules',
  'Functional Beverages',
  'Oral Liquid',
  'Gummies',
  'Jelly Sticks',
  'Tea Bags',
  'Other',
];

const quantities = [
  '1,000 – 3,000 units',
  '3,000 – 10,000 units',
  '10,000 – 50,000 units',
  '50,000+ units',
  'Not yet decided',
];

const formulaOptions = [
  'I have a specific formula',
  'I have a concept, need assistance',
  'No idea yet, please suggest',
];

const processSteps = [
  { step: '01', title: 'Submit Inquiry', desc: 'Fill in your product direction, estimated quantity, dosage form, and contact details.', icon: 'ri-file-edit-line' },
  { step: '02', title: 'Initial Response', desc: 'Kang Yong Biotech will contact you as soon as possible to confirm basic requirements.', icon: 'ri-customer-service-2-line' },
  { step: '03', title: 'Needs Assessment', desc: 'Based on your brand positioning, channel, budget and product goals, we evaluate the right formula, dosage form and packaging.', icon: 'ri-search-eye-line' },
  { step: '04', title: 'Quote & Proposal', desc: 'After confirming requirements, we provide an initial collaboration proposal, timeline and cost estimate.', icon: 'ri-price-tag-3-line' },
];

export default function EnContactFormPage() {
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useSEO({
    title: 'Contact Form | OEM ODM Health Supplement Inquiry | Kang Yong Biotech',
    description: 'Fill out the Kang Yong Biotech OEM/ODM inquiry form: product type, estimated quantity, formula status, and requirements. Our English-speaking team responds promptly to all inquiries.',
    keywords: 'OEM inquiry form, ODM inquiry form, health supplement consultation, Kang Yong contact form, manufacturing inquiry',
    lang: 'en',
    canonicalPath: '/en/contact/form',
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="requirement"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitState('loading');
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitState('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  return (
    <EnLayout>
      {/* Hero */}
      <section
        className="relative pt-24 pb-16 bg-cover bg-top"
        style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=clean%20white%20natural%20laboratory%20workspace%20with%20soft%20green%20plants%20and%20morning%20light%20streaming%20through%20large%20windows%20minimalist%20aesthetic%20healthcare%20biotech%20professional%20environment%20muted%20tones%20serene%20atmosphere&width=1600&height=560&seq=contact-form-hero&orientation=landscape')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/28 to-black/45" />
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 mb-4 text-white/60 text-[0.8125rem]">
            <Link to="/en" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/en/contact" className="hover:text-white transition-colors cursor-pointer">Contact</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/90">Contact Form</span>
          </div>
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-[0.8125rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-white/30">
            Contact
          </span>
          <h1 className="text-white font-bold text-[2.25rem] md:text-[3rem] leading-tight mb-4 max-w-2xl">
            Contact Form
          </h1>
          <p className="text-white/85 text-[1.0625rem] leading-[1.8] max-w-xl">
            Share your product development needs. Our team will evaluate the best OEM/ODM approach for your brand.
          </p>
          <div className="mt-6 h-0.5 w-14 bg-[#81C784] rounded-full" />
        </div>
      </section>

      {/* Notice bar */}
      <div className="bg-[#e8f5e9] border-y border-[#c8e6c9]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center gap-4">
          {[
            { icon: 'ri-time-line', text: 'Prompt response to your inquiry' },
            { icon: 'ri-shield-check-line', text: 'Data privacy protected' },
            { icon: 'ri-customer-service-2-line', text: 'English-speaking team' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-1.5 text-[#388E3C] text-[0.8125rem] font-medium">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-sm`} />
              </div>
              {item.text}
            </div>
          ))}
        </div>
      </div>

      {/* Process steps */}
      <section className="py-14 bg-[#f7faf7] border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="mb-8">
            <h2 className="text-[#1a2e1a] font-bold text-[1.375rem] md:text-[1.625rem] mb-2">
              What Happens After You Submit?
            </h2>
            <p className="text-gray-500 text-[0.9rem] leading-[1.75]">
              From inquiry to development, here's our clear collaboration flow.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {processSteps.map((s, idx) => (
              <div key={s.step} className="relative bg-white rounded-xl p-5 border border-gray-100">
                {idx < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-[2.25rem] left-full w-5 h-0.5 bg-[#c8e6c9] z-10" />
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#e8f5e9] rounded-full flex-shrink-0 border border-[#c8e6c9]">
                    <i className={`${s.icon} text-[#4CAF50] text-base`} />
                  </div>
                  <span className="text-[#4CAF50] font-bold text-[0.75rem] tracking-widest">STEP {s.step}</span>
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main form section */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 xl:gap-16 items-start">

            {/* Sidebar */}
            <div className="w-full lg:w-[300px] xl:w-[340px] flex-shrink-0 space-y-5">
              <div>
                <h2 className="text-[#1a2e1a] font-bold text-[1.25rem] mb-1.5">Before You Fill In</h2>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75]">
                  The following information will help you complete the form quickly and improve collaboration efficiency.
                </p>
              </div>
              {[
                { icon: 'ri-flask-line', title: 'Formula Development', desc: 'Whether it\'s a brand new formula, dosage form selection, or modifying an existing formula — we\'re here to help.' },
                { icon: 'ri-leaf-line', title: 'Natural Ingredient Sourcing', desc: 'We focus on natural ingredients and can help you select raw materials that match your brand positioning.' },
                { icon: 'ri-file-shield-2-line', title: 'Regulatory Compliance', desc: 'We assist with Taiwan FDA regulations and labeling requirements to ensure compliant market launch.' },
                { icon: 'ri-box-3-line', title: 'Packaging Design', desc: 'From container selection to visual design — full packaging planning support.' },
              ].map((tip) => (
                <div key={tip.title} className="flex items-start gap-3 bg-[#f7faf7] rounded-xl p-4 border border-gray-100">
                  <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                    <i className={`${tip.icon} text-[#4CAF50] text-base`} />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-semibold text-[0.875rem] mb-0.5">{tip.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.7]">{tip.desc}</p>
                  </div>
                </div>
              ))}

              {/* Direct contact */}
              <div className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-3">Contact Us Directly</p>
                <div className="space-y-2.5">
                  <a href="tel:+886422029858" className="flex items-center gap-2.5 text-gray-600 hover:text-[#4CAF50] transition-colors cursor-pointer text-[0.875rem]">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                      <i className="ri-phone-line text-[#4CAF50] text-sm" />
                    </div>
                    +886-4-2202-9858
                  </a>
                  <a href="mailto:natural1070606@gmail.com" className="flex items-center gap-2.5 text-gray-600 hover:text-[#4CAF50] transition-colors cursor-pointer text-[0.875rem] break-all">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0">
                      <i className="ri-mail-line text-[#4CAF50] text-sm" />
                    </div>
                    natural1070606@gmail.com
                  </a>
                </div>
              </div>

              {/* LINE sidebar */}
              <div className="bg-[#e8f5e9] border border-[#c8e6c9] rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-line-fill text-[#4CAF50] text-xl" />
                  </div>
                  <p className="text-[#1a2e1a] font-bold text-[0.9375rem]">Chat on LINE</p>
                </div>
                <p className="text-gray-600 text-[0.8125rem] leading-[1.7] mb-3">
                  Instant messaging with our English-speaking team. Add our LINE account to start a conversation.
                </p>
                <a
                  href="https://lin.ee/X7Hcpf4"
                  rel="nofollow"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-4 py-2.5 rounded-full transition-all duration-300 cursor-pointer w-full"
                >
                  <i className="ri-line-fill" />
                  Join LINE
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="w-full lg:flex-1">
              <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] mb-2">ODM/OEM Inquiry Form</h2>
              <p className="text-gray-500 text-[0.9375rem] mb-7 leading-[1.75]">
                Please fill in the fields below and we'll assign a specialist to respond to your inquiry.
              </p>

              {submitState === 'success' ? (
                <div className="flex flex-col items-center text-center bg-[#f7faf7] rounded-2xl border border-green-100 overflow-hidden">
                  <div className="w-full px-8 pt-12 pb-8">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-5 border border-green-200 mx-auto">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-3xl" />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.375rem] mb-3">Inquiry Submitted — Thank You!</h3>
                    <p className="text-gray-500 text-[0.9375rem] leading-[1.75] max-w-sm mx-auto">
                      Kang Yong Biotech will contact you shortly to discuss your product direction, dosage form, quantity and partnership model.
                    </p>
                  </div>
                  <div className="w-full bg-[#e8f5e9] border-t border-[#c8e6c9] px-6 sm:px-8 py-6">
                    <p className="text-[#1a2e1a] font-semibold text-[0.9375rem] mb-1">Want a Faster Response?</p>
                    <p className="text-gray-600 text-[0.875rem] leading-[1.75] mb-4">
                      Add us on LINE for real-time assistance from our team.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://lin.ee/X7Hcpf4"
                        rel="nofollow"
                        className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto"
                      >
                        <i className="ri-line-fill text-lg" />
                        Chat on LINE
                      </a>
                      <span className="text-[#4CAF50] text-[0.875rem] font-medium">LINE@: @126uwznp</span>
                    </div>
                  </div>
                  <div className="px-8 py-5">
                    <button
                      onClick={() => setSubmitState('idle')}
                      className="whitespace-nowrap border border-gray-300 text-gray-500 hover:border-[#4CAF50] hover:text-[#4CAF50] font-medium text-[0.875rem] px-6 py-2.5 rounded-full transition-all duration-300 cursor-pointer"
                    >
                      Submit Another Inquiry
                    </button>
                  </div>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="contact_name"
                        type="text"
                        required
                        placeholder="Your full name"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Company / Brand Name
                      </label>
                      <input
                        name="company_name"
                        type="text"
                        placeholder="Your company or brand name"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        placeholder="e.g. +1-555-000-0000"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="your@email.com"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      Product Type
                    </label>
                    <select
                      name="product_type"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>Select product type</option>
                      {productTypes.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      Estimated Quantity
                    </label>
                    <select
                      name="quantity"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>Select estimated quantity</option>
                      {quantities.map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-2">
                      Formula Status
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {formulaOptions.map((opt) => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="formula_idea"
                            value={opt}
                            className="accent-[#4CAF50] cursor-pointer"
                          />
                          <span className="text-[#1a2e1a] text-[0.875rem]">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      Requirements{' '}
                      <span className={`text-[0.75rem] font-normal ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                        ({charCount}/500)
                      </span>
                    </label>
                    <textarea
                      name="requirement"
                      rows={5}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="Please describe your needs: product direction, target audience, budget, distribution channel, or any other details."
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors resize-none bg-white"
                    />
                    {charCount > 500 && (
                      <p className="text-red-500 text-[0.75rem] mt-1">Requirements cannot exceed 500 characters</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitState === 'loading' || charCount > 500}
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] disabled:opacity-60 text-white font-bold text-[1rem] py-4 rounded-lg transition-all duration-300 cursor-pointer"
                  >
                    {submitState === 'loading' ? (
                      <><i className="ri-loader-4-line animate-spin text-lg" />Submitting…</>
                    ) : (
                      <><i className="ri-send-plane-line text-lg" />Submit Inquiry</>
                    )}
                  </button>

                  {submitState === 'error' && (
                    <p className="text-red-500 text-[0.875rem] text-center">Submission failed. Please try again or contact us directly.</p>
                  )}

                  <p className="text-gray-400 text-[0.8125rem] text-center leading-[1.75]">
                    After submitting, a specialist will contact you to discuss your product direction, dosage form, estimated quantity and collaboration process.
                  </p>

                  <div className="border-t border-gray-100 pt-5 mt-1">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#f7faf7] rounded-xl px-5 py-4 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-[#e8f5e9] rounded-full border border-[#c8e6c9] flex-shrink-0">
                          <i className="ri-line-fill text-[#4CAF50] text-lg" />
                        </div>
                        <div>
                          <p className="text-[#1a2e1a] font-semibold text-[0.875rem]">Want a Faster Reply?</p>
                          <p className="text-gray-500 text-[0.8125rem]">Add us on LINE after submitting. ID: <span className="text-[#4CAF50] font-medium">@126uwznp</span></p>
                        </div>
                      </div>
                      <a
                        href="https://lin.ee/X7Hcpf4"
                        rel="nofollow"
                        className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 w-full sm:w-auto"
                      >
                        <i className="ri-line-fill" />
                        Join LINE
                      </a>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5 bg-white rounded-2xl border border-[#c8e6c9] px-6 sm:px-8 py-6 sm:py-8 mb-10">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <div className="w-12 h-12 flex items-center justify-center bg-[#e8f5e9] rounded-full border border-[#c8e6c9] flex-shrink-0">
                <i className="ri-line-fill text-[#4CAF50] text-2xl" />
              </div>
              <div className="min-w-0">
                <p className="text-[#1a2e1a] font-bold text-[1rem] mb-0.5">Or Chat with Us on LINE</p>
                <p className="text-gray-500 text-[0.875rem] leading-[1.7]">Get a faster reply from our team. LINE@ ID: <span className="text-[#4CAF50] font-semibold">@126uwznp</span></p>
              </div>
            </div>
            <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[0.9375rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 w-full sm:w-auto">
              <i className="ri-line-fill text-lg" /> Join LINE
            </a>
          </div>
          <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">You May Also Be Interested In</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'Cooperation Process', desc: 'Clear steps from first contact to product delivery.', href: '/en/cooperation/process' },
              { title: 'FAQ', desc: 'Common questions about MOQ, formula, regulations and timelines.', href: '/en/cooperation/faq' },
              { title: 'Contact Information', desc: 'Phone, email and office address.', href: '/en/contact/info' },
            ].map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer block"
              >
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5">{item.title}</p>
                <p className="text-gray-500 text-[0.875rem] leading-[1.65]">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </EnLayout>
  );
}
